import { Alert, Box, Container, FormHelperText, Paper, Skeleton, Snackbar, Typography } from '@mui/material';
import { useEffect, useState, FC } from 'react';
import { useNavigate } from 'react-router';
import PaymentVendors from '@acorn-portal/shared/src/components/vendor/PaymentVendors';
import SimpleBraintreePaymentForm from '../../components/payment/simple-braintree-payment-form';
import { PaymentDetailsByTokenModel, PayNowClient } from '@acorn-portal/shared/src/services/api';
import PaymentHeader from '@acorn-portal/shared/src/components/header/PaymentHeader';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { CustomPaymentMethodPayload } from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';

type PaymentDetails = Omit<PaymentDetailsByTokenModel, 'validUntil' | 'directDebitId'>;

const DebtCollectionDirectPayment: FC = () => {
	const payNow = useAppSelector((redux) => redux.payNow);
	const [errorMessage, setErrorMessage] = useState<string>();
	const [openError, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [paymentToken, setPaymentToken] = useState('');
	const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>();
	const navigate = useNavigate();

	const onDirectPayment = async (payload: CustomPaymentMethodPayload) => {
		if (paymentDetails) {
			const { nonce, threeDSecureInfo, deviceData } = payload;
			const { amount, emailAddress, agreementId } = paymentDetails;
			try {
				const client = new PayNowClient(undefined, API);

				const response = await client.makeOutstandingDebtPayment({
					paymentNonce: nonce,
					amount,
					threeDSecureAuthenticationId: threeDSecureInfo ? threeDSecureInfo.threeDSecureAuthenticationId : '',
					emailAddress: emailAddress.toString(),
					deviceData,
					vaultCard: false,
					agreementId: agreementId.toString(),
				});
				if (response.isSuccessful) {
					navigate(ROUTE_PATHS.canxDirectPaymentSuccess, {
						state: {
							debtCollectionPayment: true,
						},
					});
				} else {
					setError(true);
				}
			} catch (e) {
				console.error(e);
				setError(true);
			}
		}
	};

	useEffect(() => {
		const getPaymentToken = async () => {
			setLoading(true);
			try {
				const client = new PayNowClient(undefined, API);
				const response = await client.generatePaymentToken(undefined);
				setPaymentToken(response);
			} catch (e) {
				console.error('Error generating payment token', e);
				navigate(ROUTE_PATHS.error, { replace: true });
			} finally {
				setLoading(false);
			}
		};
		const getPaymentDetailsByToken = async (token: string) => {
			try {
				const client = new PayNowClient(undefined, API);
				const result = await client.getPaymentDetailsByToken(token);
				setPaymentDetails(result);
				await getPaymentToken();
			} catch (e) {
				console.error('Error getting payment details by token', e);
				navigate(ROUTE_PATHS.error, { replace: true });
			}
		};

		const params = new URLSearchParams(window.location.search);
		const token = params.get('token');
		if (token !== null) {
			getPaymentDetailsByToken(token);
			return;
		}

		if (payNow.details) {
			setPaymentDetails({
				amount: payNow.details.unaccountedForBalance || 0,
				splitPaymentId: '',
				customerId: payNow.details.customerId || '',
				agreementId: payNow.details.agreementId || '',
				emailAddress: payNow.details.email || '',
			});

			getPaymentToken();
		}
	}, []);

	return (
		<FullPageLoadingSplash loading={loading} message='Retrieving Payment Details...'>
			<Paper
				elevation={1}
				sx={{
					margin: 1.5,
					borderRadius: '8px',
					paddingBottom: 3,
				}}
			>
				<Container maxWidth='sm'>
					<Box sx={{ padding: 1 }} />
					<Box my={4}>
						<PaymentHeader title='Secure Payment Checkout' />
						<Typography textAlign='left' sx={{ fontSize: '20px' }}>
							Payment to be taken : <b>{paymentDetails?.amount ? formatCurrency(paymentDetails?.amount) : ''}</b>
						</Typography>
						<Typography textAlign='left'>You will receive an email as receipt and confirmation of payment.</Typography>
						{paymentToken && paymentDetails ? (
							<SimpleBraintreePaymentForm
								loading={loading}
								amount={paymentDetails.amount.toString()}
								clientToken={paymentToken}
								submitPayment={onDirectPayment}
								isOptOut={false}
								setErrorMessage={(value) => setErrorMessage(value)}
							/>
						) : (
							<Skeleton variant='rectangular' width='100%' height={200} sx={{ mt: 5 }} />
						)}
						{errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
						<PaymentVendors />
					</Box>
					<Snackbar open={openError} autoHideDuration={6000} onClose={() => setError(false)}>
						<Alert onClose={() => setError(false)} severity='error' sx={{ width: '100%' }}>
							Unable to process payment.
						</Alert>
					</Snackbar>
				</Container>
			</Paper>
		</FullPageLoadingSplash>
	);
};
export default DebtCollectionDirectPayment;
